import { useCallback, useMemo } from 'react';
import { Node, Edge, Connection, useReactFlow, OnNodesChange, OnEdgesChange, OnConnect, MarkerType } from 'reactflow';
import { useFlowStore } from '../stores/flowStore';
import { shallow } from 'zustand/shallow';
import { validateConnection } from '../validation/flowValidation';
import { flowTheme } from '../theme/flowTheme';

// Default edge settings
const defaultEdgeOptions: Partial<Edge> = {
  type: 'smoothstep',
  animated: true,
  style: { strokeWidth: 2 },
  markerEnd: {
    type: MarkerType.ArrowClosed,
    width: 20,
    height: 20,
    color: '#B1B1B7',
  },
};

export function useFlow() {
  const { project } = useReactFlow();
  const {
    nodes: nodesMap,
    edges: edgesMap,
    selectedNodeId,
    isDragging,
    addNode,
    updateNode,
    removeNode,
    setNodes,
    addEdge,
    updateEdge,
    removeEdge,
    setEdges,
    setSelectedNode,
    setDragging,
    batchUpdateNodes,
    batchUpdateEdges,
  } = useFlowStore(
    (state) => ({
      nodes: state.nodes,
      edges: state.edges,
      selectedNodeId: state.selectedNodeId,
      isDragging: state.isDragging,
      addNode: state.addNode,
      updateNode: state.updateNode,
      removeNode: state.removeNode,
      setNodes: state.setNodes,
      addEdge: state.addEdge,
      updateEdge: state.updateEdge,
      removeEdge: state.removeEdge,
      setEdges: state.setEdges,
      setSelectedNode: state.setSelectedNode,
      setDragging: state.setDragging,
      batchUpdateNodes: state.batchUpdateNodes,
      batchUpdateEdges: state.batchUpdateEdges,
    }),
    shallow
  );

  // Convert Maps to arrays for React Flow
  const nodes: Node[] = useMemo(() => Array.from(nodesMap.values()), [nodesMap]);
  const edges: Edge[] = useMemo(() => Array.from(edgesMap.values()), [edgesMap]);

  const onNodesChange: OnNodesChange = useCallback(
    (changes) => {
      const updates = new Map<string, Partial<Node>>();
      
      changes.forEach((change) => {
        if (change.type === 'position' && 'position' in change) {
          updates.set(change.id, { position: change.position });
        } else if (change.type === 'select') {
          setSelectedNode(change.selected ? change.id : null);
        }
      });

      if (updates.size > 0) {
        batchUpdateNodes(updates);
      }
    },
    [batchUpdateNodes, setSelectedNode]
  );

  const onEdgesChange: OnEdgesChange = useCallback(
    (changes) => {
      changes.forEach((change) => {
        if (change.type === 'remove') {
          removeEdge(change.id);
        }
      });
    },
    [removeEdge]
  );

  const onConnect: OnConnect = useCallback(
    (connection: Connection) => {
      if (connection.source && connection.target) {
        const sourceNode = nodesMap.get(connection.source);
        const targetNode = nodesMap.get(connection.target);

        if (sourceNode && targetNode) {
          const validationResult = validateConnection(sourceNode, targetNode, Array.from(edgesMap.values()));
          if (!validationResult.valid) {
            console.warn('Invalid connection:', validationResult.error);
            return;
          }

          const edgeStyle = flowTheme.edges[sourceNode.type] || flowTheme.edges.default;
          const edge: Edge = {
            ...defaultEdgeOptions,
            ...edgeStyle,
            id: `${connection.source}-${connection.target}`,
            source: connection.source,
            target: connection.target,
            sourceHandle: connection.sourceHandle || undefined,
            targetHandle: connection.targetHandle || undefined,
          } as Edge;
          addEdge(edge);
        }
      }
    },
    [addEdge, nodesMap, edgesMap]
  );

  const onDragStart = useCallback(() => setDragging(true), [setDragging]);
  const onDragEnd = useCallback(() => setDragging(false), [setDragging]);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();

      const type = event.dataTransfer.getData('application/reactflow');
      if (!type) return;

      const { left, top } = event.currentTarget.getBoundingClientRect();
      const position = project({
        x: event.clientX - left,
        y: event.clientY - top,
      });

      const newNode: Node = {
        id: `${type}-${Date.now()}`,
        type,
        position,
        data: {
          label: `${type} ${nodes.length + 1}`,
          onLabelChange: (newLabel: string) => {
            updateNode(newNode.id, { data: { ...newNode.data, label: newLabel } });
          },
          onPropertyChange: (property: string, value: any) => {
            updateNode(newNode.id, { data: { ...newNode.data, [property]: value } });
          },
          selected: false,
        },
        dragHandle: '.node-drag-handle',
      };

      addNode(newNode);
      setSelectedNode(newNode.id);
    },
    [nodes.length, addNode, setSelectedNode, project, updateNode]
  );

  return {
    nodes,
    edges,
    selectedNodeId,
    isDragging,
    onNodesChange,
    onEdgesChange,
    onConnect,
    onDrop,
    onDragStart,
    onDragEnd,
    addNode,
    updateNode,
    removeNode,
    setNodes,
    addEdge,
    updateEdge,
    removeEdge,
    setEdges,
    setSelectedNode,
  };
}